import api from "../api";

const GOOGLE_OAUTH_STATE_KEY = "sat_google_oauth_state";
const GOOGLE_OAUTH_VERIFIER_KEY = "sat_google_oauth_verifier";
const GOOGLE_OAUTH_METADATA_KEY = "sat_google_oauth_metadata";

const base64UrlEncode = (input) => {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  let binary = "";

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

const randomBase64Url = (byteLength = 48) => {
  const randomBytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(randomBytes);
  return base64UrlEncode(randomBytes);
};

const createCodeChallenge = async (verifier) => {
  const encoded = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest("SHA-256", encoded);
  return base64UrlEncode(digest);
};

const normalizeDomain = (domain) => {
  if (!domain) return "";
  return domain.startsWith("http") ? domain : `https://${domain}`;
};

const decodeJwtPayload = (token) => {
  const payload = token.split(".")[1];
  const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
  const decoded = atob(padded);
  const json = decodeURIComponent(
    decoded
      .split("")
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join(""),
  );

  return JSON.parse(json);
};

const getRedirectUri = () => {
  return (
    process.env.REACT_APP_COGNITO_OAUTH_REDIRECT_URI ||
    `${window.location.origin}/auth/callback`
  );
};

export const getCognitoOAuthConfig = async () => {
  const response = await api.get("/api/config/cognito/");
  const config = response.data || {};

  return {
    ...config,
    OAUTH_DOMAIN: config.OAUTH_DOMAIN || process.env.REACT_APP_COGNITO_OAUTH_DOMAIN,
    GOOGLE_IDENTITY_PROVIDER:
      config.GOOGLE_IDENTITY_PROVIDER ||
      process.env.REACT_APP_COGNITO_GOOGLE_IDENTITY_PROVIDER ||
      "Google",
  };
};

export const startGoogleSignIn = async ({ allowCreate = false } = {}) => {
  const config = await getCognitoOAuthConfig();
  const domain = normalizeDomain(config.OAUTH_DOMAIN);

  if (!domain || !config.CLIENT_ID) {
    throw new Error("Google sign-in is not configured yet.");
  }

  const state = randomBase64Url(32);
  const verifier = randomBase64Url(64);
  const challenge = await createCodeChallenge(verifier);
  const redirectUri = getRedirectUri();

  sessionStorage.setItem(GOOGLE_OAUTH_STATE_KEY, state);
  sessionStorage.setItem(GOOGLE_OAUTH_VERIFIER_KEY, verifier);
  sessionStorage.setItem(
    GOOGLE_OAUTH_METADATA_KEY,
    JSON.stringify({ allowCreate, redirectUri }),
  );

  const authorizeUrl = new URL(`${domain}/oauth2/authorize`);
  authorizeUrl.search = new URLSearchParams({
    client_id: config.CLIENT_ID,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: redirectUri,
    identity_provider: config.GOOGLE_IDENTITY_PROVIDER,
    state,
    code_challenge_method: "S256",
    code_challenge: challenge,
  }).toString();

  window.location.assign(authorizeUrl.toString());
};

const cacheCognitoTokens = ({ config, tokens, idPayload, accessPayload }) => {
  const username = idPayload["cognito:username"] || idPayload.username || idPayload.sub;
  const keyPrefix = `CognitoIdentityServiceProvider.${config.CLIENT_ID}`;
  const tokenPrefix = `${keyPrefix}.${username}`;
  const nowSeconds = Math.floor(Date.now() / 1000);
  const issuedAt = idPayload.iat || accessPayload?.iat || nowSeconds;
  const clockDrift = nowSeconds - issuedAt;

  localStorage.setItem("idToken", tokens.id_token);
  localStorage.setItem(`${keyPrefix}.LastAuthUser`, username);
  localStorage.setItem(`${tokenPrefix}.idToken`, tokens.id_token);
  localStorage.setItem(`${tokenPrefix}.accessToken`, tokens.access_token);
  localStorage.setItem(`${tokenPrefix}.clockDrift`, `${clockDrift}`);

  if (tokens.refresh_token) {
    localStorage.setItem(`${tokenPrefix}.refreshToken`, tokens.refresh_token);
  }
};

export const completeGoogleSignIn = async () => {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  const errorDescription = params.get("error_description");

  if (error) {
    throw new Error(errorDescription || "Google sign-in was cancelled.");
  }

  const code = params.get("code");
  const state = params.get("state");
  const expectedState = sessionStorage.getItem(GOOGLE_OAUTH_STATE_KEY);
  const verifier = sessionStorage.getItem(GOOGLE_OAUTH_VERIFIER_KEY);
  const metadata = JSON.parse(sessionStorage.getItem(GOOGLE_OAUTH_METADATA_KEY) || "{}");

  if (!code || !state || state !== expectedState || !verifier) {
    throw new Error("Google sign-in could not be verified. Please try again.");
  }

  const config = await getCognitoOAuthConfig();
  const domain = normalizeDomain(config.OAUTH_DOMAIN);
  const redirectUri = metadata.redirectUri || getRedirectUri();

  if (!domain || !config.CLIENT_ID) {
    throw new Error("Google sign-in is not configured yet.");
  }

  try {
    const tokenResponse = await fetch(`${domain}/oauth2/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: config.CLIENT_ID,
        code,
        redirect_uri: redirectUri,
        code_verifier: verifier,
      }),
    });

    if (!tokenResponse.ok) {
      throw new Error("Google sign-in could not be completed. Please try again.");
    }

    const tokens = await tokenResponse.json();

    if (!tokens.id_token || !tokens.access_token) {
      throw new Error("Google sign-in did not return a complete session.");
    }

    const idPayload = decodeJwtPayload(tokens.id_token);
    const accessPayload = decodeJwtPayload(tokens.access_token);

    cacheCognitoTokens({ config, tokens, idPayload, accessPayload });

    return {
      allowCreate: Boolean(metadata.allowCreate),
      config,
      idPayload,
      tokens,
    };
  } finally {
    sessionStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
    sessionStorage.removeItem(GOOGLE_OAUTH_VERIFIER_KEY);
    sessionStorage.removeItem(GOOGLE_OAUTH_METADATA_KEY);
  }
};
